import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import Papa from "papaparse";

const statusColor = {
  completed: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  scheduled: "bg-blue-100 text-blue-800",
};

const InterviewTable = ({ interviews }) => {
  const [expandedRows, setExpandedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  const toggleRow = (id) => {
    setExpandedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const totalPages = Math.ceil(interviews.length / rowsPerPage);
  const paginatedInterviews = interviews.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handleExportCSV = () => {
    const dataToExport = interviews.map((interview) => ({
      ID: interview.id,
      Position: interview.position,
      Company: interview.company,
      Date: new Date(interview.date).toLocaleDateString(),
      Status: interview.status,
      Notes: interview.notes || "No notes",
    }));

    const csv = Papa.unparse(dataToExport);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "interviews.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-4 bg-white rounded-xl shadow">
      {/* Export CSV button */}
      <div className="flex justify-end mb-4">
        <button
          onClick={handleExportCSV}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
        >
          Export CSV
        </button>
      </div>

      <table className="w-full text-left border">
        <thead className="bg-gray-100">
          <tr>
            <th className="p-2">Position</th>
            <th className="p-2">Company</th>
            <th className="p-2">Date</th>
            <th className="p-2">Status</th>
            <th className="p-2">Toggle</th>
          </tr>
        </thead>
        <tbody>
          {paginatedInterviews.map((interview) => (
            <React.Fragment key={interview.id}>
              <tr className="border-b">
                <td className="p-2">{interview.position}</td>
                <td className="p-2">{interview.company}</td>
                <td className="p-2">
                  {new Date(interview.date).toLocaleDateString()}
                </td>
                <td className="p-2">
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${
                      statusColor[interview.status?.toLowerCase()] ||
                      "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {interview.status}
                  </span>
                </td>
                <td className="p-2">
                  <button onClick={() => toggleRow(interview.id)}>
                    {expandedRows.includes(interview.id) ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </button>
                </td>
              </tr>
              {expandedRows.includes(interview.id) && (
                <tr className="bg-gray-50">
                  <td colSpan="5" className="p-4 text-sm text-gray-700">
                    <strong>Note:</strong> {interview.notes || "No notes"}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="flex justify-between items-center mt-4">
        <button
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default InterviewTable;
